/**
 * Coach-Store (Herzstück): Chatverlauf, Kapitel-Kontext, Docking, Stop/Abort.
 *
 * Verhalten 1:1 zur Web-App (chatbot.js):
 *  - „Ich denke nach"-Bubble (thinking) während der Anfrage,
 *  - vollständige Antwort am Stück (KEIN Token-Streaming),
 *  - 429 → abgesetzte Kontingent-Bubble (quota) mit Server-Detail,
 *  - 401/403 → Logout (zentral im API-Client),
 *  - Abbrechen via AbortController (Send-Button wird zum Stop).
 */
import { signal } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import { ApiError } from "../api/client";
import type { ChatHistorySession } from "../api/types";
import { isAuthenticated } from "./session";

export type MsgKind = "" | "thinking" | "quota";

export interface CoachMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  kind: MsgKind;
}

export interface CoachContext {
  courseId: string;
  moduleId: string | null;
  blockId: string | null;
  title: string;
}

/** 'closed' = nicht sichtbar, 'sheet' = Bottom-Sheet über der App. */
export type CoachDock = "closed" | "sheet";

export const messages = signal<CoachMessage[]>([]);
export const context = signal<CoachContext | null>(null);
export const dock = signal<CoachDock>("closed");
export const expanded = signal<boolean>(false);
export const busy = signal<boolean>(false);
/**
 * Text, der beim nächsten Öffnen ins Eingabefeld eingesetzt werden soll (NICHT
 * gesendet) — für „Frag die Kurs-KI"-Fragen mit [Platzhaltern]. Das CoachPanel
 * konsumiert ihn (setzt das Feld, markiert den ersten Platzhalter) und nullt ihn.
 */
export const pendingInsert = signal<string | null>(null);

// --- Chat-Verlauf (Nachlesen): letzte N Sitzungen vom Server, ueber den Live-
// Nachrichten angezeigt. Reines Anzeigen, keine KI. 1:1 zur Web-App (chatbot.js).
export const historySessions = signal<ChatHistorySession[]>([]);
export const historyHasOlder = signal<boolean>(false);
export const pendingDeleteAll = signal<boolean>(false);
export const pendingDeleteKey = signal<string | null>(null);
const HISTORY_SESSIONS = 3;
let historyLoaded = false; // einmal pro App-Sitzung laden (kein Doppel-Anzeigen)
let historyLoading = false;
let historyLoadPromise: Promise<void> | null = null;

export function sessionKey(s: ChatHistorySession): string {
  return `${s.from_id}-${s.to_id}`;
}

let counter = 0;
const nextId = () => ++counter;
let abortController: AbortController | null = null;
let greeted = false;

function push(role: "user" | "assistant", content: string, kind: MsgKind = ""): number {
  const id = nextId();
  const next = [...messages.value, { id, role, content, kind }];
  // Verlauf deckeln wie im Web (max 30).
  messages.value = next.length > 30 ? next.slice(next.length - 30) : next;
  return id;
}

function remove(id: number): void {
  messages.value = messages.value.filter((m) => m.id !== id);
}

function recentUserQuestions(limit = 3): string[] {
  return messages.value
    .filter((m) => m.role === "user")
    .map((m) => m.content.trim())
    .filter(Boolean)
    .slice(-limit);
}

export function setContext(ctx: CoachContext | null): void {
  context.value = ctx;
}

function ensureGreeting(): void {
  if (greeted || messages.value.length) return;
  greeted = true;
  // Web-Parität (chatbot.js): vor dem Login nur allgemeine Begrüßung, keine Kapiteltexte.
  push(
    "assistant",
    isAuthenticated.value
      ? "Hallo, ich bin dein Kursbegleiter. Frag mich zum aktuellen Kapitel oder zu KI allgemein."
      : "Hallo. Vor dem Login kann ich dir allgemein erklären, worum es im Kurs geht. Kursinhalte zeige ich erst nach dem Login.",
  );
}

export function openSheet(): void {
  ensureGreeting();
  void loadHistory();
  dock.value = "sheet";
}
export function closeCoach(): void {
  dock.value = "closed";
  resetHistoryPrompts();
}
/** Tab-Taste / Coach-Icon: offen → schließen, zu → öffnen (Toggle-Overlay). */
export function toggleCoach(): void {
  if (dock.value === "sheet") closeCoach();
  else openSheet();
}
export function toggleExpanded(): void {
  expanded.value = !expanded.value;
}
export function resetCoach(): void {
  messages.value = [];
  greeted = false;
  historySessions.value = [];
  historyHasOlder.value = false;
  historyLoaded = false;
  resetHistoryPrompts();
}

// --- Chat-Verlauf: laden + loeschen ---------------------------------------
export function resetHistoryPrompts(): void {
  pendingDeleteAll.value = false;
  pendingDeleteKey.value = null;
}

/**
 * Verlauf der letzten N Sitzungen laden (nur eingeloggt, einmal pro App-Sitzung).
 * Stilles Scheitern: der Coach funktioniert auch ohne Verlauf normal weiter; bei
 * 401/403 loggt der API-Client bereits zentral aus.
 */
export function loadHistory(force = false): Promise<void> {
  if (!isAuthenticated.value) return Promise.resolve();
  if (historyLoading) return historyLoadPromise ?? Promise.resolve();
  if (historyLoaded && !force) return Promise.resolve();
  historyLoading = true;
  const ctx = context.value;
  historyLoadPromise = (async () => {
    try {
      const res = await endpoints.chatHistory(ctx?.courseId, HISTORY_SESSIONS);
      historySessions.value = Array.isArray(res.sessions) ? res.sessions : [];
      historyHasOlder.value = !!res.has_older;
      historyLoaded = true;
    } catch {
      /* 401/403 -> Client hat ausgeloggt; sonst Verlauf bleibt leer */
    } finally {
      historyLoading = false;
    }
  })();
  return historyLoadPromise;
}

/** Eine einzelne Sitzung (id-Bereich) loeschen. */
export async function deleteHistorySession(fromId: number, toId: number): Promise<void> {
  const ctx = context.value;
  try {
    await endpoints.chatHistoryDeleteSession(fromId, toId, ctx?.courseId);
    historySessions.value = historySessions.value.filter(
      (s) => !(s.from_id === fromId && s.to_id === toId),
    );
  } catch (err) {
    if (!(err instanceof ApiError && (err.status === 401 || err.status === 403))) {
      push("assistant", "Die Sitzung konnte nicht gelöscht werden.");
    }
  } finally {
    resetHistoryPrompts();
  }
}

/** Ganzen Verlauf loeschen (Doppel-Bestaetigung im UI). */
export async function deleteAllHistory(): Promise<void> {
  const ctx = context.value;
  try {
    await endpoints.chatHistoryDeleteAll(ctx?.courseId);
    historySessions.value = [];
    historyHasOlder.value = false;
    messages.value = [];
    resetHistoryPrompts();
    push("assistant", "Dein Chat-Verlauf wurde gelöscht. Wir können jederzeit neu starten.");
  } catch (err) {
    resetHistoryPrompts();
    if (!(err instanceof ApiError && (err.status === 401 || err.status === 403))) {
      push("assistant", "Der Verlauf konnte nicht gelöscht werden.");
    }
  }
}

/** Laufende Antwort abbrechen (Stop-Button). */
export function stopCoach(): void {
  abortController?.abort();
}

/** 429-Hinweis wie im Web: Server-detail + ggf. Freischaltzeit aus quota.reset_at. */
function formatQuota(err: ApiError): string {
  const body = (err.body ?? {}) as { detail?: string; quota?: { reset_at?: string } };
  if (body.detail) return String(body.detail).trim();
  const parts = [
    "Dein KI-Kontingent ist gerade aufgebraucht.",
    "Du kannst den Kurs weiter lesen und z. B. ChatGPT, Gemini oder Claude nutzen.",
  ];
  const reset = body.quota?.reset_at;
  if (reset) {
    try {
      parts.push("Wieder freigeschaltet: " + new Date(reset).toLocaleString("de-DE"));
    } catch {
      /* ignore */
    }
  }
  return err.detail || parts.join("\n\n");
}

/** Frage an den Coach (mit Kapitel-Kontext). */
export async function ask(text: string): Promise<void> {
  const content = text.trim();
  if (!content) return;
  if (busy.value) {
    push("assistant", "Bitte warte kurz, bis die aktuelle Antwort fertig ist, oder stoppe sie zuerst.");
    return;
  }
  ensureGreeting();
  // Verlauf-Snapshot zuerst abschliessen, sonst koennte die neue Nachricht oben
  // als History UND unten als Live-Nachricht doppelt erscheinen (vgl. Web-Fix).
  await loadHistory();
  const recentQuestions = recentUserQuestions(3);
  push("user", content);
  busy.value = true;
  const thinkingId = push("assistant", "", "thinking");
  abortController = new AbortController();
  const ctx = context.value;
  try {
    const res = await endpoints.chat(
      {
        message: content,
        course_id: ctx?.courseId,
        module_id: ctx?.moduleId,
        block_id: ctx?.blockId,
        page: typeof location !== "undefined" ? location.pathname.replace(/^\//, "") || "app" : "app",
        recent_questions: recentQuestions,
      },
      abortController.signal,
    );
    remove(thinkingId);
    push("assistant", res.answer || "Ich habe leider keine Antwort erhalten.");
  } catch (err) {
    remove(thinkingId);
    if (err instanceof DOMException && err.name === "AbortError") {
      push("assistant", "Antwort abgebrochen.");
    } else if (err instanceof ApiError && err.status === 429) {
      push("assistant", formatQuota(err), "quota");
    } else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      /* Client hat bereits ausgeloggt; nichts anzeigen. */
    } else {
      push(
        "assistant",
        err instanceof ApiError ? err.detail : "Der KI Coach ist gerade nicht erreichbar.",
      );
    }
  } finally {
    busy.value = false;
    abortController = null;
  }
}

/** Leitfrage aus einer Fragenkarte: Sheet öffnen + direkt fragen. */
export async function askQuestion(questionText: string): Promise<void> {
  openSheet();
  await ask(questionText);
}

/**
 * Frage mit [Platzhaltern]: Coach öffnen und Text ins Eingabefeld setzen, OHNE zu
 * senden — der Nutzer ersetzt erst die Platzhalter (1:1 zu KiKursChat.insert).
 */
export function insertIntoCoach(text: string): void {
  const content = text.trim();
  if (!content) return;
  ensureGreeting();
  dock.value = "sheet";
  pendingInsert.value = content;
}

/**
 * Prompt mit [Platzhaltern]: Eingabemaske öffnen statt nur einzufügen — pro Platzhalter
 * eine Frage (Text/Sprache), fertiger Prompt → kopieren oder an die Kurs-KI senden.
 * 1:1 zum Web (oeffnePromptDialog). Signal-gesteuert; <PromptDialog/> (App.tsx) liest es.
 */
export const pendingPromptDialog = signal<string | null>(null);
export function openPromptDialog(text: string): void {
  const content = text.trim();
  if (!content) return;
  pendingPromptDialog.value = content;
}
